/**
 * The current access token, reachable from modules that are not React components.
 *
 * `api/http.ts` attaches the Authorization header for every call in one place, and it is
 * a plain module rather than a hook, so the token is published here by a provider-level
 * effect instead of being threaded through the API layer as an argument.
 *
 * What lives here is usually not a usable credential: with the OIDC service worker
 * running, the library hands out a placeholder that the worker swaps for the real token
 * on its way to the network. Without the worker it is the real one. Either way the value
 * is what the request must carry, and nothing else in the app needs to know which case
 * it is in.
 */

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token || null;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/**
 * Return `headers` with Authorization added, or unchanged when there is no token.
 *
 * Unauthenticated is deliberately not an error here: the request goes out bare and the
 * backend answers 401, which is the same path a revoked user takes and is therefore the
 * one already handled, rather than a second failure mode that only exists on the client.
 */
export function withAuth(headers: HeadersInit | undefined): HeadersInit | undefined {
  const token = getAccessToken();
  if (!token) return headers;
  return { ...(headers as Record<string, string> | undefined), Authorization: `Bearer ${token}` };
}
